import { DOMAIN_PATTERN } from "./accessRules";

// Where a published Trust Center lives.
//   regodit – our domain: <Trust Center host>/trust/<company-domain>
//   custom  – a subdomain the customer owns, pointed at us with a CNAME
// Both hosts are build settings, so infra can move them without a code
// change:
//   REACT_APP_TRUST_CENTER_URL           public link host, e.g.
//                                        https://trust.regodit.ai. Unset:
//                                        the app's own origin.
//   REACT_APP_TRUST_CENTER_CNAME_TARGET  what a customer's CNAME points at.
const trimSlash = (url) => String(url || "").replace(/\/+$/, "");

export const trustCenterBaseUrl = () =>
  trimSlash(process.env.REACT_APP_TRUST_CENTER_URL) || window.location.origin;

// TODO: confirm the real CNAME target with infra (placeholder default).
export const CNAME_TARGET =
  process.env.REACT_APP_TRUST_CENTER_CNAME_TARGET || "trust.regodit.com";

// Domains that belong to Regodit: never a customer's custom domain.
const OWN_DOMAINS = ["regodit.com", "regodit.ai"];
const isOwnDomain = (domain) =>
  OWN_DOMAINS.some((own) => domain === own || domain.endsWith(`.${own}`));

// Link names that could clash with our own paths or hosts. Keep in step
// with regodit-api service/domain_rules.js (the server refuses them).
export const RESERVED_SLUGS = [
  "admin",
  "api",
  "app",
  "assets",
  "callback",
  "demo",
  "dev",
  "help",
  "login",
  "logout",
  "mail",
  "preview",
  "static",
  "status",
  "support",
  "trust",
  "www",
];
export const isReservedSlug = (slug) =>
  RESERVED_SLUGS.includes(String(slug || "").toLowerCase());

export const PUBLISH_TARGETS = {
  regodit: { label: "Regodit domain" },
  custom: { label: "Your own domain" },
};

// "https://Trust.Acme.com/path" → "trust.acme.com"
export const normalizeDomain = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .replace(/[/?#].*$/, "")
    .replace(/\.$/, "");

// Two-part endings where "acme.co.in" is still a main domain, not a subdomain.
const TWO_PART_SUFFIXES = [
  "co.in",
  "org.in",
  "net.in",
  "firm.in",
  "gen.in",
  "ind.in",
  "ac.in",
  "gov.in",
  "co.uk",
  "org.uk",
  "com.au",
  "net.au",
  "com.sg",
  "co.nz",
  "co.za",
  "com.br",
];

const isMainDomain = (domain) => {
  const labels = domain.split(".");
  const suffix = labels.slice(-2).join(".");
  return labels.length < (TWO_PART_SUFFIXES.includes(suffix) ? 4 : 3);
};

// Returns null when the domain can be used, or a message for the admin.
// With `companyDomain` (Company Settings) it must be a subdomain of it, like
// trust.acme.com for acme.com. Same rules as the API (domain_rules.js).
export const validateCustomDomain = (value, companyDomain) => {
  const domain = normalizeDomain(value);
  if (!domain) return "Enter the domain you want to use, like trust.acme.com.";
  if (!DOMAIN_PATTERN.test(domain)) return `"${domain}" is not a valid domain.`;
  // A root domain (acme.com) cannot have a CNAME record.
  if (isMainDomain(domain))
    return `Use a subdomain, like trust.${domain}. A main domain cannot point to us.`;
  if (domain === CNAME_TARGET || isOwnDomain(domain))
    return "Use a domain your company owns.";
  if (companyDomain !== undefined) {
    const base = normalizeDomain(companyDomain).replace(/^www\./, "");
    if (!base)
      return "Add your company domain in Company Settings first. Your custom domain must be part of it.";
    if (!domain.endsWith(`.${base}`))
      return `Use a subdomain of your company domain, like trust.${base}.`;
  }
  return null;
};

// Where a connected domain is in going live (the API's customDomainStage).
// `label` replaces the status pill's word while it is being set up.
export const DOMAIN_STAGES = {
  waiting_for_dns: {
    help: "Waiting for your DNS record. Add the CNAME below. New records are usually seen within an hour.",
  },
  issuing_certificate: {
    label: "Setting up",
    help: "We found your DNS record and are getting a secure (HTTPS) certificate for the domain. This usually takes a few minutes.",
  },
  deploying: {
    label: "Setting up",
    help: "The certificate is ready. We are turning the domain on across our network. This usually takes a few minutes.",
  },
  live: {
    help: "Live over HTTPS. Your Trust Center opens on this domain while it is published.",
  },
  failed: {
    help: "We could not finish setting up this domain.",
  },
};

// The public link on Regodit's host.
export const regoditUrl = (companyDomain) =>
  companyDomain ? `${trustCenterBaseUrl()}/trust/${companyDomain}` : "";

// The admin's preview always opens on the app's own origin: signing in
// (Auth0) is only set up there, not on a separate Trust Center host.
export const previewUrl = (companyDomain) =>
  companyDomain
    ? `${window.location.origin}/trust/${companyDomain}?preview=1`
    : "";

export const customUrl = (customDomain) =>
  customDomain ? `https://${customDomain}` : "";

// Custom domain status, as shown in Settings → Custom domain.
export const DOMAIN_STATUSES = {
  unknown: {
    label: "Unknown",
    help: "We have not checked yet. Click Check status after you add the DNS record.",
  },
  pending: {
    label: "Pending",
    help: "We could not find the CNAME record yet. DNS changes can take up to 24 hours.",
  },
  misconfigured: {
    label: "Wrong record",
    help: "The domain points somewhere else. Change its CNAME record to the value below.",
  },
  active: {
    label: "Connected",
    help: "The record is correct. Once published, your Trust Center opens on this domain.",
  },
};

// Looks up the CNAME record with Cloudflare's public DNS-over-HTTPS service.
// TODO: move to the API, which must also issue the TLS certificate and map
// the hostname to this customer before the domain can really serve the page.
// Hosts that run the Regodit app itself: the app, /trust/<slug> links and
// the admin preview. Any other host is a customer's own domain pointed at us
// with a CNAME, and shows that customer's Trust Center at its root.
// REACT_APP_APP_HOSTS (comma separated) adds more app hosts if needed.
const APP_HOST_SUFFIXES = [...OWN_DOMAINS, "cloudfront.net", "amazonaws.com"];
export const isCustomDomainHost = (hostname = window.location.hostname) => {
  const host = String(hostname || "").toLowerCase();
  if (!host || host === "localhost" || host.endsWith(".localhost")) return false;
  if (/^[\d.]+$/.test(host) || host.includes(":")) return false; // IP address
  const extra = (process.env.REACT_APP_APP_HOSTS || "")
    .split(",")
    .map((h) => h.trim().toLowerCase())
    .filter(Boolean);
  if (extra.includes(host)) return false;
  return !APP_HOST_SUFFIXES.some(
    (own) => host === own || host.endsWith(`.${own}`)
  );
};

// "Resources: 1 added, 2 changed" for each section with unpublished changes.
export const describeChanges = (changes = []) =>
  changes.map((c) => {
    const parts = [
      c.added && `${c.added} added`,
      c.removed && `${c.removed} removed`,
      c.changed && `${c.changed} changed`,
    ].filter(Boolean);
    return `${c.label}: ${parts.join(", ")}`;
  });
